import React, {
  useState,
  useMemo,
  useCallback,
  useEffect,
  useRef,
} from "react";

import {
  Eye,
  EyeOff,
  CheckCircle,
  AlertCircle,
  Users,
  Upload,
  MessageSquare,
  Menu,
  X,
  FileText,
  User,
} from "lucide-react";
import { useNavigate } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import placeholder from "../assets/placeholder.jpg";
import AddDocumentForm from "../pages/Admin/Students/Add-Doc";
import LogoutSection from "../assets/logout";
import Header from "../assets/Header";
import Footer from "../assets/Footer";
import Messages from "../pages/Admin/Students/Messages";
import MessageThread from "../pages/Admin/Students/MessageThread";
import Profile from "../assets/profile";
import ChangeP from "../assets/changepassword";
import EditStudent from "../pages/Edit_student(student)";
import logo from "../assets/logo.png";

// Theme constants
const THEME = {
  primary: "#2563eb",
  secondary: "#1e40af",
  background: "#f8fafc",
  sidebar: "#1f2937",
  card: "#ffffff",
  textPrimary: "#1e293b",
  textSecondary: "#64748b",
  success: "#10b981",
  warning: "#f59e0b",
  error: "#ef4444",
  borderRadius: "12px",
  shadow: "0 4px 6px rgba(0,0,0,0.05), 0 1px 3px rgba(0,0,0,0.1)",
  shadowMd: "0 8px 25px rgba(0,0,0,0.1)",

  // Header colors
  header: {
    background: "linear-gradient(to right, #0c4a6e, #3730a3, #1f2937)",
    textPrimary: "#f1f5f9",
    textSecondary: "#dbeafe",
    border: "rgba(30, 64, 175, 0.4)",
    highlight: "#22c55e",
    logoShadow: "0 0 6px rgba(59, 130, 246, 0.5)",
    hoverBg: "rgba(55, 65, 81, 0.7)",
    focusRing: "rgba(96, 165, 250, 1)",
  },
};

const spring = { type: "spring", stiffness: 200, damping: 24 };

export default function StudentDashboard() {
  const [activeSection, setActiveSection] = useState("profile");
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [changeStatus, setChangeStatus] = useState(null);
  const [showCurrent, setShowCurrent] = useState(false);
  const [showNew, setShowNew] = useState(false);
  const [showRepeat, setShowRepeat] = useState(false);
  const [reminderData, setReminderData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [editingStudentId, setEditingStudentId] = useState(null);

  const navigate = useNavigate();
  const sidebarRef = useRef(null);
  const hamburgerRef = useRef(null);

  const goToProfile = () => {
    setActiveSection("profile");
  };

  // Toggle sidebar function
  const toggleSidebar = useCallback(() => {
    setSidebarOpen((prev) => !prev);
  }, []);

  // Close sidebar function
  const closeSidebar = useCallback(() => {
    setSidebarOpen(false);
  }, []);

  // Handle responsive sidebar state
  const [isDesktop, setIsDesktop] = useState(false);

  useEffect(() => {
    const checkDevice = () => {
      const desktop = window.innerWidth >= 768;
      setIsDesktop(desktop);
      // On desktop, start with sidebar closed for full width
      if (desktop) {
        setSidebarOpen(false);
      }
    };

    checkDevice();
    window.addEventListener("resize", checkDevice);
    return () => window.removeEventListener("resize", checkDevice);
  }, []);

  // Close sidebar when clicking outside or on navigation item

  // Handle escape key to close sidebar
  useEffect(() => {
    const handleEscape = (event) => {
      if (event.key === "Escape") {
        closeSidebar();
      }
    };

    document.addEventListener("keydown", handleEscape);
    return () => document.removeEventListener("keydown", handleEscape);
  }, [closeSidebar]);

  const student = useMemo(() => {
    const userData = JSON.parse(localStorage.getItem("user")) || {};
    return {
      id: userData.id || "N/A",
      studentId: userData.id_s || "N/A",
      name: userData.name || "",
      role: userData.role || "",
      university: userData.university || "N/A",
      course: userData.course || "N/A",
      intake: userData.intake || "N/A",
      status: userData.applicationStatus,
      email: userData.email || "N/A",
      mobile: userData.mobile || "N/A",
    };
  }, []);

  const handleStudentNameClick = useCallback(() => {
      setEditingStudentId(student.studentId);
      localStorage.setItem('student_id', student.studentId);
      setActiveSection('edit-student');
    }, [student.studentId]);
  
    // Handle edit student close
    const handleEditStudentClose = useCallback(() => {
      setEditingStudentId(null);
      setActiveSection('students');
    }, []);
  
    // Handle edit student save
    const handleEditStudentSave = useCallback((updatedStudent) => {
      // Update localStorage with new student data
      const currentUser = JSON.parse(localStorage.getItem("user")) || {};
      const updatedUser = { ...currentUser, ...updatedStudent };
      localStorage.setItem("user", JSON.stringify(updatedUser));
      
      setEditingStudentId(null);
      setActiveSection('students');
      
      // Optional: Show success message
      setChangeStatus({
        type: "success",
        message: "Student profile updated successfully!"
      });
      
      // Clear success message after 3 seconds
      setTimeout(() => {
        setChangeStatus(null);
      }, 3000);
    }, []);

  useEffect(() => {
    if (!student.studentId) return;

    const fetchReminder = async () => {
      try {
        setLoading(true);

        const token = localStorage.getItem("token");

        const res = await fetch(
          `${process.env.REACT_APP_BACKEND_URL}/api/reminder/${student.studentId}`,
          {
            headers: {
              "Content-Type": "application/json",
              Authorization: `Bearer ${token}`,
            },
          }
        );

        if (!res.ok) {
          throw new Error(`HTTP error! Status: ${res.status}`);
        }

        const data = await res.json();
        setReminderData(data);
      } catch (err) {
        setError(err.message);
      } finally {
        setLoading(false);
      }
    };

    fetchReminder();
  }, [student.studentId]);

  // Handle navigation with sidebar close on mobile
  const handleNavigation = useCallback(
    (section) => {
      setActiveSection(section);
      if (!isDesktop) {
        closeSidebar();
      }
    },
    [isDesktop, closeSidebar]
  );

  const renderContent = useCallback(() => {
    if (activeSection === "edit-student" && editingStudentId) {
      return (
        <EditStudent
          studentId={editingStudentId}
          onClose={handleEditStudentClose}
          onSave={handleEditStudentSave}
        />
      );
    }

    function InfoField({ label, value }) {
      return (
        <div>
          <label className="block text-sm font-semibold text-gray-500 mb-1">
            {label}
          </label>
          <p className="text-gray-900 font-medium">{value}</p>
        </div>
      );
    }
    if (activeSection === "students") {
      return (
        <div className="space-y-6">
          {/* Header Section */}
          <div className="border-b border-gray-200 pb-4">
            <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-3">
              <div>
                <h1 className="text-2xl font-bold text-gray-900 mb-1">
                  My Application Status
                </h1>
                <p className="text-gray-600 text-sm">
                  Track your application progress and important reminders
                </p>
              </div>
            </div>
          </div>

          {/* Application Card */}
          <motion.div
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            className="bg-white rounded-lg shadow-md border border-gray-200 overflow-hidden hover:shadow-lg transition-shadow"
          >
            {/* Card Header */}
            <div className="bg-gradient-to-r from-gray-50 to-gray-100 px-6 py-4 border-b border-gray-200">
              <h2 className="text-lg font-semibold text-gray-900">Application Details</h2>
            </div>

            {/* Card Content - Grid Layout */}
            <div className="p-6 grid grid-cols-1 md:grid-cols-2 gap-6">
              {/* Left Column */}
              <div className="space-y-4">
                <div>
                  <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-1">Student ID</p>
                  <p
                    onClick={handleStudentNameClick}
                    className="text-lg font-bold text-blue-600 cursor-pointer hover:text-blue-800 hover:underline transition-colors"
                  >
                    {student.studentId}
                  </p>
                </div>
                <div>
                  <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-1">Full Name</p>
                  <p
                    onClick={handleStudentNameClick}
                    className="text-lg font-bold text-gray-900 cursor-pointer hover:text-blue-600 hover:underline transition-colors"
                  >
                    {student.name}
                  </p>
                </div>
                <div>
                  <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-1">Email</p>
                  <p className="text-gray-700">{student.email}</p>
                </div>
                <div>
                  <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-1">Mobile</p>
                  <p className="text-gray-700">{student.mobile || "N/A"}</p>
                </div>
              </div>

              {/* Right Column */}
              <div className="space-y-4">
                <div>
                  <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-1">University</p>
                  <p className="text-gray-900 font-medium">{student.university}</p>
                </div>
                <div>
                  <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-1">Course</p>
                  <p className="text-gray-900 font-medium">{student.course}</p>
                </div>
                <div>
                  <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-1">Intake</p>
                  <p className="text-gray-900 font-medium">{student.intake}</p>
                </div>
                <div>
                  <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-1">Application Status</p>
                  <span
                    className={`inline-flex items-center px-3 py-1.5 rounded-full text-sm font-semibold border ${
                      student.status === "Incomplete Application"
                        ? "bg-yellow-50 text-yellow-700 border-yellow-200"
                        : student.status === "Complete Application"
                        ? "bg-green-50 text-green-700 border-green-200"
                        : student.status === "Conditional Offer"
                        ? "bg-blue-50 text-blue-700 border-blue-200"
                        : student.status === "Rejected"
                        ? "bg-red-50 text-red-700 border-red-200"
                        : student.status === "Unconditional Offer"
                        ? "bg-emerald-50 text-emerald-700 border-emerald-200"
                        : student.status === "Enroll"
                        ? "bg-purple-50 text-purple-700 border-purple-200"
                        : "bg-gray-50 text-gray-600 border-gray-200"
                    }`}
                  >
                    {student.status}
                  </span>
                </div>
              </div>
            </div>

            {/* Card Footer - Action Button */}
            <div className="bg-gray-50 border-t border-gray-200 px-6 py-4 flex justify-end">
              <motion.button
                onClick={handleStudentNameClick}
                whileHover={{ scale: 1.02 }}
                whileTap={{ scale: 0.98 }}
                className="flex items-center gap-2 bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded-lg shadow-sm transition-colors font-medium"
              >
                <FileText size={16} />
                View Full Application
              </motion.button>
            </div>
          </motion.div>

          {/* Reminder Section */}
          {reminderData?.reminder ? (
            <motion.div
              initial={{ opacity: 0, y: -10 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.4 }}
              className={`rounded-lg flex items-start gap-4 p-5 shadow-md border-l-4 transition-all duration-300 ${
                reminderData.days_left <= 3
                  ? "bg-red-50 text-red-800 border-red-500"
                  : reminderData.days_left <= 7
                  ? "bg-yellow-50 text-yellow-800 border-yellow-500"
                  : "bg-blue-50 text-blue-800 border-blue-500"
              }`}
            >
              <div className="flex-shrink-0 mt-0.5">
                {reminderData.days_left <= 3 ? (
                  <AlertCircle size={24} className="font-bold" />
                ) : (
                  <CheckCircle size={24} />
                )}
              </div>
              <div className="flex-1">
                <p className="font-bold text-lg">
                  {reminderData.days_left <= 3
                    ? "🚨 Urgent Action Required"
                    : "📅 Upcoming Deadline"}
                </p>
                <p className="text-sm mt-1">{reminderData.message}</p>
                <div className="mt-3">
                  <span className="inline-block px-3 py-1 rounded-full bg-white/70 text-sm font-semibold">
                    ⏱️ {reminderData.days_left} day{reminderData.days_left !== 1 ? "s" : ""} remaining
                  </span>
                </div>
              </div>
            </motion.div>
          ) : (
            <motion.div
              initial={{ opacity: 0, y: -10 }}
              animate={{ opacity: 1, y: 0 }}
              className="flex flex-col items-center justify-center py-12 text-center bg-gradient-to-br from-blue-50 to-indigo-50 rounded-lg border border-blue-200"
            >
              <div className="w-16 h-16 rounded-full bg-blue-100 flex items-center justify-center text-blue-600 mb-4 shadow-sm">
                <CheckCircle size={32} />
              </div>
              <p className="font-semibold text-gray-800 text-lg mb-1">
                No Upcoming Reminders 🎉
              </p>
              <p className="text-sm text-gray-600 max-w-sm">
                You're all set! Important deadlines and reminders will appear here when they're needed.
              </p>
            </motion.div>
          )}
        </div>
      );
    }

    if (activeSection === "profile") {
      return (
        <div className="bg-white rounded-2xl shadow-sm p-4 md:p-6 w-full max-w-6xl mx-auto">
          <h2 className="text-xl md:text-2xl font-bold mb-6 text-gray-800">
            Student Profile
          </h2>

          {changeStatus && (
            <div
              className={`mb-4 p-3 rounded-lg ${
                changeStatus.type === "success"
                  ? "bg-green-100 text-green-700 border border-green-200"
                  : "bg-red-100 text-red-700 border border-red-200"
              }`}
            >
              {changeStatus.message}
            </div>
          )}

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 md:gap-6 mb-6">
            <ProfileField label="Student ID" value={student.studentId} />
            <ProfileField label="Name" value={student.name} />
            <ProfileField label="Email" value={student.email} />
            <ProfileField label="University" value={student.university} />
            <ProfileField label="Course" value={student.course} />
            <ProfileField label="Intake" value={student.intake} />
            <div className="md:col-span-2">
              <ProfileField label="Status" value={student.status} />
            </div>
            <ProfileField label="Mobile" value={student.mobile} />
          </div>

          <hr className="my-6" />
          <h3 className="text-lg md:text-xl font-semibold text-gray-800 mb-4">
            Change Password
          </h3>
          <ChangeP email={student?.email} />
        </div>
      );
    }

    if (activeSection === "upload") {
      return (
        <div className="w-full max-w-6xl mx-auto">
          <h2 className="text-xl md:text-2xl font-bold mb-6 text-gray-800">
            Document Upload
          </h2>
          <AddDocumentForm
            theme={THEME}
            isStudentView={true}
            studentName={student.name}
            studentId={student.studentId}
          />
        </div>
      );
    }

    if (activeSection === "messages") {
      return (
        <div className="w-full">
          <Messages
            student={student}
            theme={THEME}
            setActiveSection={setActiveSection}
          />
        </div>
      );
    }

    if (activeSection.startsWith("message-thread/")) {
      const threadId = activeSection.split("/")[1];
      return (
        <div className="w-full">
          <MessageThread
            threadId={threadId}
            student={student}
            theme={THEME}
            setActiveSection={setActiveSection}
          />
        </div>
      );
    }

    return (
      <div className="flex flex-col items-center justify-center py-12 text-center w-full">
        <User size={48} className="text-gray-300 mb-4" />
        <p className="text-gray-500 text-lg font-medium">
          Select an option to begin
        </p>
        <p className="text-gray-400 text-sm mt-2">
          Choose from the sidebar menu to view different sections
        </p>
      </div>
    );
  }, [activeSection, student, changeStatus, reminderData, editingStudentId, handleEditStudentClose, handleEditStudentSave, handleStudentNameClick]);

  const ProfileField = ({ label, value }) => (
    <div>
      <label className="block text-sm font-semibold text-gray-500 mb-2">
        {label}
      </label>
      <div className="text-gray-800 border border-gray-200 rounded-xl px-4 py-3 bg-gray-50 text-base">
        {value}
      </div>
    </div>
  );

  const InfoField = ({ label, value }) => (
    <div>
      <label className="block text-sm font-semibold text-gray-500 mb-1">
        {label}
      </label>
      <div className="text-gray-800 text-base">{value}</div>
    </div>
  );

  const StatusBadge = ({ status }) => {
    const getStatusStyles = (status) => {
      const baseStyles =
        "px-3 py-1.5 rounded-full text-xs font-semibold border";

      switch (status) {
        case "Incomplete Application":
          return `${baseStyles} bg-yellow-50 text-yellow-700 border-yellow-200`;
        case "Complete Application":
          return `${baseStyles} bg-green-50 text-green-700 border-green-200`;
        case "Conditional Offer":
          return `${baseStyles} bg-blue-50 text-blue-700 border-blue-200`;
        case "Rejected":
          return `${baseStyles} bg-red-50 text-red-700 border-red-200`;
        case "Unconditional Offer":
          return `${baseStyles} bg-green-100 text-green-800 border-green-300`;
        case "Enroll":
          return `${baseStyles} bg-blue-50 text-blue-700 border-blue-200`;
        default:
          return `${baseStyles} bg-gray-50 text-gray-600 border-gray-200`;
      }
    };

    return <span className={getStatusStyles(status)}>{status}</span>;
  };

  // Hamburger Menu Button Component - Visible on all screens
  const HamburgerMenu = useMemo(
    () => (
      <motion.button
        ref={hamburgerRef}
        onClick={toggleSidebar}
        className="
  p-2 rounded-xl bg-blue-600/20 
  text-blue-300 
  backdrop-blur-md 
  border border-blue-500/20
  hover:bg-blue-600/30 
  hover:text-white
  transition-all duration-300
  focus:outline-none 
  focus:ring-2 focus:ring-blue-400/50
  active:scale-95
"
        aria-label={sidebarOpen ? "Close menu" : "Open menu"}
        whileTap={{ scale: 0.95 }}
      >
        <AnimatePresence mode="wait">
          {sidebarOpen ? (
            <motion.div
              key="close"
              initial={{ rotate: -90, opacity: 0 }}
              animate={{ rotate: 0, opacity: 1 }}
              exit={{ rotate: 90, opacity: 0 }}
              transition={{ duration: 0.2 }}
            >
              <X size={20} />
            </motion.div>
          ) : (
            <motion.div
              key="menu"
              initial={{ rotate: 90, opacity: 0 }}
              animate={{ rotate: 0, opacity: 1 }}
              exit={{ rotate: -90, opacity: 0 }}
              transition={{ duration: 0.2 }}
            >
              <Menu size={20} />
            </motion.div>
          )}
        </AnimatePresence>
      </motion.button>
    ),
    [sidebarOpen, toggleSidebar]
  );

  // Top bar for mobile
  const TopBar = useMemo(
    () => (
      <motion.div
        className="fixed top-0 left-0 right-0 z-40 p-4 flex items-center justify-between md:hidden bg-white shadow-sm border-b border-gray-200"
        initial={{ y: -100 }}
        animate={{ y: 0 }}
        transition={{ type: "spring", stiffness: 300, damping: 30 }}
      >
        {HamburgerMenu}
        <div className="flex items-center gap-2">
          {reminderData?.reminder && reminderData.days_left <= 3 && (
            <span
              className="w-2 h-2 rounded-full animate-pulse bg-red-500"
              title="You have urgent reminders"
            ></span>
          )}
          <span className="text-sm font-medium text-gray-700">
            Hello, {student.name.split(" ")[0] || "Student"}
          </span>
        </div>
      </motion.div>
    ),
    [student.name, reminderData, HamburgerMenu]
  );

  // Sidebar profile snippet
  const UserProfile = useMemo(
    () => (
      <motion.div
        className="relative flex flex-col items-center bg-gradient-to-br from-gray-800 via-gray-900 to-black p-4 rounded-2xl border border-gray-700 min-h-[180px] w-full"
        initial={{ opacity: 0, y: -20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.2, duration: 0.5 }}
      >
        {/* Profile Image */}
        <div className="relative">
          <Profile userId={student.id} />
        </div>

        {/* User Name */}
        <h2 className="mt-3 text-white text-base font-semibold text-center px-2 line-clamp-2">
          {student.name}
        </h2>

        {/* Role */}
        <p className="mt-1 text-xs text-blue-300 bg-blue-900/30 px-3 py-1 rounded-full">
          {student.role}
        </p>

        {/* Reminder indicator in sidebar */}
        {reminderData?.reminder && (
          <div className="mt-2 text-xs text-center">
            <span
              className={`px-2 py-1 rounded-full text-white ${
                reminderData.days_left <= 3
                  ? "bg-red-500 animate-pulse"
                  : "bg-yellow-500"
              }`}
            >
              {reminderData.days_left} day
              {reminderData.days_left !== 1 ? "s" : ""} left
            </span>
          </div>
        )}
      </motion.div>
    ),
    [student, reminderData]
  );

  // Sidebar navigation
  const SidebarContent = useMemo(
    () => (
      <div className="overflow-y-auto flex flex-col h-full">
        {/* Mobile Header */}
        <div className="md:hidden flex items-center justify-between p-4 border-b border-gray-700">
          <span className="text-white font-semibold">Menu</span>
          {HamburgerMenu}
        </div>

        {/* User Profile Section */}
        <div className="px-3 py-4">{UserProfile}</div>

        {/* Navigation Menu */}
        <nav className="px-2 py-2 flex-grow space-y-1">
          {[
            {
              key: "profile",
              icon: User,
              label: "My Profile",
            },
            {
              key: "students",
              icon: FileText,
              label: "My Status",
              hasNotification:
                reminderData?.reminder && reminderData.days_left <= 7,
            },
            {
              key: "upload",
              icon: Upload,
              label: "Upload Documents",
            },
            {
              key: "messages",
              icon: MessageSquare,
              label: "Messages",
            },
          ].map(({ key, icon: Icon, label, hasNotification }) => (
            <motion.button
              key={key}
              onClick={() => handleNavigation(key)}
              className={`w-full text-left flex items-center justify-between gap-3 text-sm py-3 px-3 transition-colors duration-300 rounded-xl ${
                activeSection === key
                  ? `bg-blue-600 text-white shadow-md`
                  : `hover:bg-gray-700 text-gray-200`
              }`}
              whileHover={{ scale: 1.02 }}
              whileTap={{ scale: 0.98 }}
              transition={spring}
              aria-current={activeSection === key ? "page" : undefined}
            >
              <div className="flex items-center gap-3">
                <Icon size={18} aria-hidden="true" />
                <span className="truncate">{label}</span>
              </div>
              {/* Notification indicator */}
              {hasNotification && (
                <span className="w-2 h-2 bg-red-500 rounded-full animate-pulse"></span>
              )}
            </motion.button>
          ))}
        </nav>

        {/* Logout Section */}
        <div className="mt-auto p-3">
          <LogoutSection navigate={navigate} />
        </div>
      </div>
    ),
    [
      UserProfile,
      activeSection,
      navigate,
      reminderData,
      HamburgerMenu,
      handleNavigation,
    ]
  );

  return (
    <div className="flex flex-col md:flex-row min-h-screen bg-gray-50">
      {TopBar}

      <AnimatePresence>
        {/* Backdrop overlay - show on mobile when sidebar is open */}
        {sidebarOpen && !isDesktop && (
          <motion.div
            className="fixed inset-0 bg-black bg-opacity-50 z-30 md:hidden"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onClick={closeSidebar}
          />
        )}
      </AnimatePresence>

      {/* Sidebar */}
      <motion.aside
        ref={sidebarRef}
        className={`fixed inset-y-0 left-0 z-40 bg-gray-900 shadow-xl flex flex-col
              w-64 md:relative md:flex-shrink-0`}
        initial={false}
        animate={{
          x: sidebarOpen ? 0 : -260, // FULL animation control
          width: isDesktop
            ? sidebarOpen
              ? 256 // 64 * 4 (Tailwind 'w-64')
              : 0
            : 256,
        }}
        transition={{
          type: "spring",
          stiffness: 260,
          damping: 30,
        }}
      >
        {SidebarContent}
      </motion.aside>

      {/* Main Content - This will stretch to full width when sidebar is closed */}
      <div
        className={`flex flex-col flex-1 overflow-hidden transition-all duration-300 ${
          isDesktop && sidebarOpen ? "md:ml-0" : "md:ml-0"
        } pt-16 md:pt-0 min-h-screen`}
      >
        {/* Desktop Header with Hamburger - Always visible on desktop */}
        <div
          className="hidden md:flex relative items-center 
  bg-gray-900 
  shadow-lg border-b border-gray-800/40 py-3 px-6"
        >
          {/* LEFT SECTION — Hamburger + Reminder */}
          <div className="flex items-center gap-4">
            {/* Hamburger menu */}
            {HamburgerMenu}

            <div className="flex items-center gap-3">
              {/* Logo */}
              <img
                src={`${logo}`}
                alt="GCRD Logo"
                className="h-7 w-auto transition-all duration-300 hover:scale-110 drop-shadow-lg filter "
              />
            </div>

            {/* 🔔 Reminder Indicator (urgent) */}
            {reminderData?.reminder && reminderData.days_left <= 3 && (
              <span
                className="relative flex h-3 w-3"
                title="You have urgent reminders"
              >
                <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-red-500 opacity-75"></span>
                <span className="relative inline-flex rounded-full h-3 w-3 bg-red-600"></span>
              </span>
            )}
          </div>

          {/* FLEX SPACER pushes right section to the end */}
          <div className="flex-1"></div>

          {/* RIGHT SECTION — Online + Greeting */}
          <div className="flex items-center gap-3">
            {/* 🟢 Online Indicator */}
            <span className="relative flex h-2.5 w-2.5">
              <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-green-400 opacity-75"></span>
              <span className="relative inline-flex rounded-full h-2.5 w-2.5 bg-green-500"></span>
            </span>

            {/* 👋 Greeting */}
            <span className="text-sm font-medium text-gray-200">
              Hello, {student.name.split(" ")[0] || "Student"}
            </span>
          </div>
        </div>

        <motion.header
          className="px-4 md:px-6 pt-4 md:pt-6"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1, transition: { duration: 0.6 } }}
        >
          <h1 className="text-lg md:text-2xl font-bold text-gray-800 flex flex-wrap items-center gap-2">
            <span>Dashboard</span>
            <span className="text-gray-600 font-normal text-sm md:text-lg">
              -{" "}
              {activeSection === "profile"
                ? " My Profile"
                : activeSection === "students"
                ? " My Status"
                : activeSection === "edit-student"
                ? " Edit Profile"
                : activeSection === "upload"
                ? " Upload Documents"
                : activeSection.startsWith("message-thread/")
                ? " Message Thread"
                : activeSection === "messages"
                ? " Messages"
                : " Edit Information"}
            </span>
          </h1>
        </motion.header>

        <motion.main
          className="flex-1 px-4 pb-4 md:px-6 md:pb-6 overflow-y-auto w-full"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ duration: 0.3 }}
        >
          <div className="bg-white rounded-2xl shadow-sm p-4 md:p-6 min-h-[calc(100vh-12rem)] w-full">
            {renderContent()}
          </div>
        </motion.main>

        <Footer />
      </div>
    </div>
  );
}